import React from 'react'
import { DateFns, YearMonthFns } from '../utils/datetime'
import { OverrideComponentProps } from '../utils/overridable-component-helper'
import { YearMonthPickerContext } from './year-month-root'
import { composeEventHandlers } from '../utils/compose-event-handlers'
import { composeRefs } from '../utils/compose-refs'

export type YearMonthPickerMonthProps<Tag extends React.ElementType<any>> = OverrideComponentProps<
  Tag,
  {
    /** format like 2024-01 */
    yearMonth: string
    selected: boolean
    active: boolean
  }
>

export const YearMonthPickerMonth = React.forwardRef<HTMLDivElement, YearMonthPickerMonthProps<any>>(
  ({ yearMonth, as = 'button', className, children, ...props }, forwardRef) => {
    const context = React.useContext(YearMonthPickerContext)

    if (context) {
      const ref = React.useRef<any>(null)
      React.useEffect(() => {
        if (context.focus === yearMonth && ['none', 'init'].includes(context.transitionState)) {
          ref.current?.focus()
        }
      }, [context.focus, context.transitionState, yearMonth, ref])
      const slots = {
        yearMonth,
        selected: context.value === yearMonth,
        active: context.focus === yearMonth
      }
      const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent<any>) => {
          if (!context.focus) return
          console.log(event)
          const moveFocus = (months: number) => {
            const newDate = YearMonthFns.addMonths(context.focus!, months)
            console.log(newDate)
            context.setFocus(newDate)
          }
          if (event.key === 'ArrowDown') {
            moveFocus(2)
          }
          if (event.key === 'ArrowUp') {
            moveFocus(-2)
          }
          if (event.key === 'ArrowLeft') {
            moveFocus(-1)
          }
          if (event.key === 'ArrowRight') {
            moveFocus(1)
          }
        },
        [context]
      )
      return React.createElement(
        as,
        {
          ...props,
          ref: composeRefs(forwardRef, ref),
          onKeyDown: composeEventHandlers(props.onKeyDown, onKeyDown),
          onClick: composeEventHandlers(props.onClick, () => context.onChange(yearMonth)),
          className: typeof className === 'function' ? className(slots) : className
        },
        ...React.Children.toArray(typeof children === 'function' ? children(slots) : children)
      )
    } else {
      throw new Error('Cannot find year month picker context')
    }
  }
) as WithForwardRefType

YearMonthPickerMonth.displayName = 'YearMonthPickerMonth'

interface WithForwardRefType extends React.FC<YearMonthPickerMonthProps<React.ElementType<any>>> {
  <T extends React.ElementType<any> = 'button'>(
    props: YearMonthPickerMonthProps<T>
  ): ReturnType<React.FC<YearMonthPickerMonthProps<T>>>
}
